/**
 * The app's route → page-title map (pure — no React, no DOM).
 *
 * Lifted out of `components/PlatformTitle.jsx` so code that is not a React
 * component can name a page the same way the browser tab does — the Page
 * Performance console prints "Task Monitor" over `/task-monitor` with the
 * very same rule, and a Node test can import it without pulling in React.
 * `PlatformTitle` imports it from here.
 *
 * Lookup order (see `titleForPath`):
 *   1. exact match, after id segments (uuid / ObjectId / numeric) and route
 *      placeholders (`:id`, `:spaceId`, `:param` …) are all collapsed to
 *      `:id` — so `/app/goals/3f2c…`, `/app/goals/:id` and `/app/goals/:goalId`
 *      all read the `/app/goals/:id` entry. A plain word never stands in for
 *      `:id`, so a key with its placeholders dropped (`/app/spaces/lists`)
 *      does not hit an id entry;
 *   2. fallback — the last meaningful segment, title-cased, with whole-word
 *      acronyms kept upper-case ("sop-integration" → "SOP Integration").
 *
 * Titles prefer the sidebar label people click; where a page has no sidebar
 * row, its visible heading.
 */

import { isIdSegment } from './page-perf/route-key.js'

/** Routes whose title is not simply their (title-cased) last path segment. */
export const PATH_TITLES = {
  // ── Public / pre-login ───────────────────────────────────────────────
  '/sign-in': 'Sign In',
  '/sign-up': 'Sign Up',
  '/forgot-password': 'Forgot Password',
  '/join': 'Join Company',

  // ── Company Admin / Sub Admin panel (AdminLayout) ────────────────────
  '/dashboard': 'Dashboard',
  '/task-monitor': 'Task Monitor',
  '/files-links': 'Files & Links',
  '/work-reports': 'Work Reports',
  '/work-roles': 'Work Roles',
  '/reminders-schedule': 'Reminders & Schedule',
  '/gamification-settings': 'Gamification',
  '/email-logs': 'Email Status Logs',
  '/support-tickets': 'Support Tickets',
  '/support-tickets/:id': 'Support Tickets',
  '/feature-suggestions/:id': 'Feature Suggestions',
  '/ai-usage': 'AI Usage',
  '/api-keys': 'AI Provider Keys',
  '/referrals': 'Refer & Earn',
  // The user menu item and the page heading both read "My Preferences".
  '/preferences': 'My Preferences',

  // ── Shared /app pages (RoleAwareLayout) ──────────────────────────────
  '/app/spaces/:id': 'Spaces',
  '/app/spaces/:id/lists/:id': 'Space List',
  '/app/board/:id': 'Kanban Board',
  '/app/documents/:id': 'Document',
  '/app/our-products': 'Our Products',
  '/app/user-manual': 'User Manual',
  '/app/goals': 'Goals',
  '/app/goals/:id': 'Goal',
  '/app/reports/daily': 'Daily Report',
  '/app/reports/weekly': 'Weekly Report',
  '/app/reports/monthly': 'Monthly Report',

  // ── Member panel (UserLayout) ────────────────────────────────────────
  '/app': 'Dashboard',
  '/app/reports/team': 'Team Reports',
  '/app/files-links': 'Files & Links',
  '/app/preferences': 'My Preferences',

  // ── Super Admin panel ────────────────────────────────────────────────
  '/super-admin/companies/:id': 'Company',
  '/super-admin/email-templates/new': 'New Email Template',
  '/super-admin/email-templates/:id/edit': 'Edit Email Template',
  '/super-admin/email-logs': 'Email Status Logs',
  '/super-admin/support-tickets/:id': 'Support Tickets',
  '/super-admin/preferences': 'My Preferences',
}

/** Panel roots: on their own past the first segment (`/app/404`,
 *  `/super-admin/:id`) they name no page, so they get no title. */
const PANEL_ROOTS = new Set(['app', 'super-admin', 'admin'])

/** Whole hyphen-separated words that read as acronyms, not title case. */
const ACRONYMS = {
  ai: 'AI',
  api: 'API',
  sop: 'SOP',
  sops: 'SOPs',
  sso: 'SSO',
  ip: 'IP',
}

/** A bare id — the same uuid / ObjectId / numeric rule Page Performance keys
 *  routes by — or a route placeholder (`:id`, `:spaceId`, `:param`). */
function isIdOrPlaceholder(segment) {
  return segment.startsWith(':') || isIdSegment(segment)
}

/** "sop-integration" → "SOP Integration"; "work-roles" → "Work Roles". */
function titleCase(segment) {
  return segment
    .split('-')
    .map((w) =>
      Object.hasOwn(ACRONYMS, w.toLowerCase())
        ? ACRONYMS[w.toLowerCase()]
        : w.charAt(0).toUpperCase() + w.slice(1),
    )
    .join(' ')
}

/**
 * The page title for a pathname or a Page Performance route key. Returns
 * null at the root, so the caller shows the platform name alone.
 */
export function titleForPath(pathname) {
  const raw = String(pathname ?? '').split(/[?#]/)[0]
  const segments = raw.split('/').filter(Boolean)
  if (segments.length === 0) return null

  const keyed = segments.map((s) => (isIdOrPlaceholder(s) ? ':id' : s))
  const exact = PATH_TITLES[`/${keyed.join('/')}`]
  if (exact) return exact

  // A bare id says nothing useful — name the section before it instead.
  const named = segments.filter((s) => !isIdOrPlaceholder(s))
  const meaningful = named.pop()
  if (!meaningful) return null
  // "/app/404" is a panel root and nothing else — no page to name.
  if (named.length === 0 && segments.length > 1 && PANEL_ROOTS.has(meaningful)) return null
  return titleCase(meaningful)
}
